import { useMemo } from "react";
import { useData, useDrivers } from "../store/dataStore";
import { pipelineCoverage, stepConversion } from "../query/metrics";
import { fmtMoney, fmtPct, fmtNum } from "../query/format";
import { KpiCard } from "../components/KpiCard";
import { Drillable } from "../components/Drillable";
import { DriverCell } from "../components/DriverCell";

const FUNNEL = ["Lead", "Qualified", "Proposal", "Negotiation", "Closed Won"];

function computePipeline(tables, quotaOverrides) {
  const bookings = tables.fact_bookings || [];
    const open = bookings.filter((b) => b.status === "open");
    const base = FUNNEL.map((stage) => {
      const rows = bookings.filter((b) => b.stage === stage);
      return { stage, count: rows.length, amount: rows.reduce((s, r) => s + r.amount, 0) };
    });
    const stages = stepConversion(base);
    const teamQuota = (tables.dim_rep || []).reduce((s, r) => s + (quotaOverrides[r.rep_id] ?? r.quota), 0);
    const stageWeight = { Lead: 0.1, Qualified: 0.25, Proposal: 0.5, Negotiation: 0.75 };
    const weighted = open.reduce((s, d) => s + d.amount * (stageWeight[d.stage] || 0.1), 0);
    return { stages, openPipeline: open.reduce((s, d) => s + d.amount, 0), openCount: open.length, teamQuota, weighted };
}

export default function Pipeline() {
  const tables = useData((s) => s.tables);
  const { targetCoverage, setDriver, quotaOverrides } = useDrivers();
  const { stages, openPipeline, openCount, teamQuota, weighted } = useMemo(() => computePipeline(tables, quotaOverrides), [tables, quotaOverrides]);

  const coverage = pipelineCoverage(openPipeline, teamQuota);
  const maxCount = Math.max(...stages.map((s) => s.count), 1);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black tracking-tight" style={{ color: "var(--navy)" }}>Pipeline</h2>
        <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
          Coverage = openPipeline / quota. Target coverage is a driver:{" "}
          <DriverCell testId="driver-target-coverage" value={targetCoverage} display={`${targetCoverage}x`} onSave={(v) => setDriver("targetCoverage", v)} />
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard testId="kpi-coverage" label="Pipeline Coverage" value={`${fmtNum(coverage, 2)}x`} sub={`Target ${targetCoverage}x`} status={coverage >= targetCoverage ? "pass" : coverage >= targetCoverage * 0.8 ? "watch" : "miss"} progress={coverage / targetCoverage} module="pipeline" seed={[]} />
        <KpiCard testId="kpi-open-pipeline" label="Open Pipeline" value={fmtMoney(openPipeline)} sub={`${openCount} open opportunities`} module="pipeline" seed={[]} />
        <KpiCard testId="kpi-weighted-pipeline" label="Weighted Pipeline" value={fmtMoney(weighted)} sub="Stage-weighted expectation" module="pipeline" seed={[]} />
        <KpiCard testId="kpi-quota" label="Team Quota" value={fmtMoney(teamQuota)} sub="Denominator (driver-adjusted)" />
      </div>

      <div className="panel p-5" data-testid="funnel">
        <div className="text-[11px] uppercase tracking-widest font-bold mb-4" style={{ color: "var(--slate)" }}>Funnel — click a stage to drill to segments → reps → opportunities</div>
        <div className="space-y-3">
          {stages.map((s, i) => (
            <Drillable key={s.stage} module="pipeline" seed={[{ field: "stage", value: s.stage }]} testId={`drill-stage-${s.stage.replace(/\s/g, "-")}`}>
              <div className="flex items-center gap-4 p-2 rounded-md">
                <div className="w-28 text-sm font-semibold" style={{ color: "var(--navy)" }}>{s.stage}</div>
                <div className="flex-1 h-8 rounded-md overflow-hidden" style={{ background: "var(--bg)" }}>
                  <div className="h-full flex items-center px-3 text-xs font-bold text-white rounded-md transition-all duration-500"
                    style={{ width: `${Math.max(8, (s.count / maxCount) * 100)}%`, background: s.stage === "Closed Won" ? "var(--good)" : `linear-gradient(90deg, var(--navy3), var(--teal-d))` }}>
                    {s.count}
                  </div>
                </div>
                <div className="w-24 text-right num text-sm" style={{ color: "var(--navy)" }}>{fmtMoney(s.amount)}</div>
                <div className="w-24 text-right num text-xs" style={{ color: i === 0 ? "var(--slate)" : s.conversion >= 0.5 ? "var(--good)" : "var(--warn)" }}>
                  {i === 0 ? "—" : `${fmtPct(s.conversion * 100, 0)} conv`}
                </div>
              </div>
            </Drillable>
          ))}
        </div>
      </div>

      <div className="panel overflow-x-auto">
        <table className="dtable" data-testid="stage-table">
          <thead><tr><th>Stage</th><th className="text-right">Opportunities</th><th className="text-right">Pipeline $</th><th className="text-right">Step Conversion</th></tr></thead>
          <tbody>
            {stages.map((s, i) => (
              <tr key={s.stage}>
                <td>
                  <Drillable module="pipeline" seed={[{ field: "stage", value: s.stage }]} testId={`drill-stage-row-${i}`} className="inline-block">
                    <span className="fact-cell font-semibold pr-4">{s.stage}</span>
                  </Drillable>
                </td>
                <td className="text-right num" style={{ color: "var(--navy)" }}>{s.count}</td>
                <td className="text-right">
                  <Drillable module="pipeline" seed={[{ field: "stage", value: s.stage }]} testId={`drill-stage-amt-${i}`} className="inline-block">
                    <span className="fact-cell pr-4">{fmtMoney(s.amount)}</span>
                  </Drillable>
                </td>
                <td className="text-right num" style={{ color: "var(--slate)" }}>{i === 0 ? "—" : fmtPct(s.conversion * 100, 1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
